import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a product select event by replacing the current media gallery with a new one.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    const eventSelectedOptions = Array.isArray(event.selectedOptions) ? event.selectedOptions : [];
    const selectedValues = eventSelectedOptions.map((option) => option?.value).filter(Boolean);
    if (event.target instanceof HTMLInputElement || event.target instanceof HTMLSelectElement) {
      const selectedOption =
        event.target instanceof HTMLSelectElement ? event.target.options[event.target.selectedIndex] : event.target;
      if (selectedOption?.value) selectedValues.push(selectedOption.value);
    } else if (event.target instanceof Element) {
      const checkedOptions = event.target.querySelectorAll('select option:checked, fieldset input:checked');
      checkedOptions.forEach((option) => {
        if (option instanceof HTMLOptionElement || option instanceof HTMLInputElement) {
          if (option.value) selectedValues.push(option.value);
        }
      });
    }

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;

        this.replaceWith(newMediaGallery);
        requestAnimationFrame(() => newMediaGallery.selectBestMatchingVariantImage?.(selectedValues));
        setTimeout(() => newMediaGallery.selectBestMatchingVariantImage?.(selectedValues), 250);
        setTimeout(() => newMediaGallery.selectBestMatchingVariantImage?.(selectedValues), 750);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /**
   * Selects a product image whose alt text matches the selected variant label.
   * This is a fallback for imported products where Shopify variants do not have
   * dedicated featured media assigned, but the product gallery images are named.
   *
   * @param {string[]} selectedValues
   */
  selectBestMatchingVariantImage(selectedValues) {
    if (!selectedValues.length) return;

    const slides = this.slideshow?.slides;
    if (!slides?.length) return;

    const normalizedValues = selectedValues.map((value) => normalizeVariantMediaText(value)).filter(Boolean);
    let candidateIndex = slides.findIndex((slide) => {
      const image = slide.querySelector('img');
      const alt = normalizeVariantMediaText(image?.getAttribute('alt') || '');

      return normalizedValues.some((value) => alt.includes(value));
    });

    if (candidateIndex < 0) {
      candidateIndex = slides.findIndex((slide) => {
        const image = slide.querySelector('img');
        const alt = normalizeVariantMediaText(image?.getAttribute('alt') || '');

        return alt.includes('farbenubersicht') || alt.includes('variantenubersicht');
      });
    }

    if (candidateIndex >= 0) {
      this.slideshow?.select(candidateIndex, undefined, { animate: false });
    }
  }

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}

/**
 * @param {string} text
 */
function normalizeVariantMediaText(text) {
  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\b(wie abgebildet|standardversand|variante|ramitos|sonnenbrille|schmuck|set|design)\b/g, ' ')
    .replace(/[^\p{L}\p{N}]+/gu, ' ')
    .trim();
}
